import React, { useEffect, useState } from "react";
import { Button } from "../../atom/Button";
import { Input } from "../../atom/Input";
import { useTranslation } from "react-i18next";
import { TRANSLATION_CONFIG } from "../../../utils/consts/translation";
import { validateEmail } from "../../../utils/validations";
import { useAuth } from "../../../utils/contexts/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../utils/consts/routes";
import { usePostResource } from "../../../services";
import { ArrowRight, View, ViewOff } from "@carbon/icons-react";

export const Login = () => {
  const { t } = useTranslation(TRANSLATION_CONFIG.appNamespace);
  const { login, isAuthenticated } = useAuth();
  const { updateResource } = usePostResource("/auth/login");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.dashboard);
    }
  }, [isAuthenticated]);

  const onEmailBlur = () => {
    if (!validateEmail(email)) {
      setErrorMessage(t("Invalid email format"));
    } else {
      setErrorMessage("");
    }
  };

  const onPasswordBlur = () => {
    if (password.trim() === "") {
      setErrorMessage(t("Password cannot be empty"));
    } else {
      setErrorMessage("");
    }
  };

  const handleLogin = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    if (!email || !password) {
      setErrorMessage(t("Email or password cannot be empty"));
      return;
    }

    if (errorMessage) {
      return;
    }

    setErrorMessage("");
    const results = await updateResource({
      username: email,
      password,
      grant_type: "password",
      client_id: import.meta.env.VITE_APP_CLIENT_ID,
      client_secret: import.meta.env.VITE_APP_CLIENT_SECRET,
    });

    const access_token = results?.data?.access_token;

    if (access_token) {
      const expires_in = results?.data?.expires_in;
      const refresh_token = results?.data?.refresh_token;
      const userid = results?.data?.user?.id;

      localStorage.setItem("tokenExpiresAt", `${Date.now() + expires_in * 1000}`);
      localStorage.setItem("refreshToken", refresh_token);
      localStorage.setItem("userid", userid);
      login(access_token);
      navigate(ROUTES.dashboard);
    }
  };

  return (
    <div className="flex items-center justify-center bg-white">
      <div className="flex flex-col justify-center items-center min-w-[360px] max-w-[360px] mt-24 gap-8">
        <div className="flex flex-col justify-center items-center gap-[120px]">
          <img src="/images/logo.svg" className="h-[24px]" alt="" />
          <div className="flex flex-col justify-center items-center gap-3">
            <p className="font-semibold text-[32px] leading-[40.32px] tracking-[-0.1px] text-black text-center">
              {t("Log in to your account")}
            </p>
            <p className="font-light text-[16px] leading-[22.4px] tracking-[0.2px] text-gray-550">
              {t("Welcome! Please enter your details.")}
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center w-full gap-6">
          <div className="w-full">
            <Input
              id="email"
              type="email"
              value={email}
              placeholder={t("Enter your email")}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={onEmailBlur}
              inputClassName="rounded-b-none"
            />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              placeholder={t("Enter your password")}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={onPasswordBlur}
              icon={showPassword ? <ViewOff size={16} /> : <View size={16} />}
              onIconClick={() => setShowPassword((prev) => !prev)}
              inputClassName="rounded-t-none"
            />
            {errorMessage && (
              <p className="text-red-500 font-normal text-[14px] leading-[19.6px] tracking-[0px] mt-1">
                {errorMessage}
              </p>
            )}
          </div>
          <div className="w-full">
            <Button
              type="primary"
              label={t("Log in")}
              onClick={(e) => handleLogin(e)}
              leftIcon={<ArrowRight size={20} />}
              additionalClassName="flex items-center justify-center w-full"
            />
          </div>
        </div>

        <div className="flex justify-start mt-1">
          <Link
            to={ROUTES.forgotPassword}
            className="text-ominimo-400 font-semibold text-[16px] leading-[20.16px] tracking-[0.2px] cursor-pointer hover:underline hover:text-ominimo-900">
            {t("Forgot Password?")}
          </Link>
        </div>
      </div>
    </div>
  );
};
